/**
 * This component is used to display a message when there is no data available
 * in the overview table. It shows the same headers as the main table for consistency.
 */
import type { FC } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe } from '@/hooks/use-me';
import { plural } from '@/utils/format/label';

import { TooltipAdditionalInfo } from '../tooltip-additional-info';

import { NumberTag } from './overview-table';

export type EmptyOverviewTableProps = {
  showSohColumns: boolean;
};

export const EmptyOverviewTable: FC<EmptyOverviewTableProps> = ({
  showSohColumns,
}) => {
  const me = useMe();

  return (
    <Table striped dense bleed className="w-full table-auto">
      <TableHead className="sticky top-0 bg-gray-100">
        <TableRow>
          <TableHeader className="text-center">ID</TableHeader>
          <TableHeader className="text-center">Coverage Status</TableHeader>
          <TableHeader className="text-center">Compliance Status</TableHeader>
          <TableHeader narrow className="text-center">
            <TooltipAdditionalInfo
              content={
                <>
                  Number of {plural(me.config.unit.labels.name).toLowerCase()}{' '}
                  having performance / operation issues
                </>
              }
            >
              {plural(me.config.unit.labels.name)} /w issues
            </TooltipAdditionalInfo>
          </TableHeader>
          <TableHeader wide className="text-center">
            Suggested Action
          </TableHeader>
          <TableHeader narrow className="text-center">
            Age in Years
          </TableHeader>
          <TableHeader narrow className="text-center">
            Total Cycles
          </TableHeader>
          <TableHeader narrow className="text-center">
            Total Throughput
          </TableHeader>
          {showSohColumns && (
            <TableHeader narrow className="text-center">
              <TooltipAdditionalInfo
                content={
                  <>
                    Defined as minimum SOH of underlying{' '}
                    {plural(me.config.unit.labels.name).toLowerCase()}
                  </>
                }
              >
                Current min. SOH
              </TooltipAdditionalInfo>
            </TableHeader>
          )}
          {showSohColumns && (
            <TableHeader narrow className="text-center">
              <TooltipAdditionalInfo
                content={
                  <>
                    Defined as
                    <br />
                    <NumberTag text="guaranteed SOH - penalties" />
                    <br />
                    (excluding predicted penalties)
                  </>
                }
              >
                Guaranteed SOH
              </TooltipAdditionalInfo>
            </TableHeader>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell colSpan={10} className="py-8 text-center">
            <p className="text-center text-gray-500">
              Currently, there is no warranty data available for this site.
            </p>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
